import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Phone,
  CalendarPlus,
  PhoneOutgoing,
  CalendarCheck,
  Stethoscope,
  Heart,
  FileText,
  Star,
  ShieldCheck,
  ClipboardList,
  Globe,
  PhoneCall,
  Send,
  SearchCheck,
  FileInput,
  CreditCard,
  AlertTriangle,
  Receipt,
  TrendingDown,
  Server,
  Users,
  Headphones,
  DollarSign,
  Mail,
  UserCheck,
  MessageCircle,
  RefreshCw,
  UserX,
  Layers,
  FileSearch,
  Database,
  Calculator,
  PiggyBank,
  Moon,
  Clock,
  ChevronDown,
} from "lucide-react";

const frontOfficeTasks = [
  {
    text: "Answer every inbound phone call",
    icon: Phone,
    steps: [
      "Staff picks up ~300 calls/month, one at a time",
      "Puts current caller on hold to go pull the chart",
      "After 5pm, Saturdays, and during lunch → straight to voicemail",
      "~35% of calls go unanswered; 75% of missed callers never call back",
      "Every missed new-patient call = ~$850 of first-year LTV walking to a competitor",
    ],
  },
  {
    text: "Schedule patients into the right slots",
    icon: CalendarPlus,
    steps: [
      "Read clinical note to figure out procedure, provider, and operatory needed",
      "Cross-check 2–3 provider schedules and equipment availability",
      "Manually resolve conflicts (double-bookings, block mismatches)",
      "Type appointment into PMS, hope nothing was missed",
      "Human error creates 10–15% of daily schedule inefficiency",
    ],
  },
  {
    text: "Fill cancellation gaps in real-time",
    icon: RefreshCw,
    steps: [
      "Cancellation hits — staff opens a spreadsheet waitlist",
      "Calls patients one by one looking for someone who matches procedure + time",
      "Most calls go to voicemail, rarely filled the same day",
      "Average practice loses $60K–$120K/yr in unfilled chair time",
    ],
  },
  {
    text: "Confirm appointments & chase no-shows",
    icon: CalendarCheck,
    steps: [
      "Export tomorrow’s schedule every afternoon",
      "Call / text every patient individually to confirm",
      "No way to distinguish low-risk from high-risk no-show patients",
      "Manual confirmations still yield 15–30% no-show rates",
    ],
  },
  {
    text: "Recall overdue hygiene patients",
    icon: Stethoscope,
    steps: [
      "Run “overdue patients” report — often 800+ names deep",
      "Call through the list one by one; most don’t pick up",
      "No bandwidth to personalize outreach, so drip campaigns die",
      "35–45% of patients lapse; each 10% improvement = $50K–$100K/yr",
    ],
  },
  {
    text: "Reactivate dormant patients (12+ months)",
    icon: UserX,
    steps: [
      "Pull list of patients inactive 12+ months",
      "Call each to find out why they stopped coming in",
      "Handle feedback (cost, bad experience, moved, insurance change) live on the phone",
      "Conversion is low; most offices stop after a week and give up",
    ],
  },
  {
    text: "Nurture treatment plans to case acceptance",
    icon: Heart,
    steps: [
      "Call patients with unaccepted treatment plans",
      "Re-explain the clinical reason, urgency, and cost on the spot",
      "Handle financial objections with no payment-plan toolkit handy",
      "No follow-up cadence — average practice closes only 34% of presented tx",
    ],
  },
  {
    text: "Chase unscheduled treatment",
    icon: FileSearch,
    steps: [
      "Pull diagnosed-but-unscheduled-treatment report",
      "Manually prioritize by urgency and dollar value",
      "Call patients one by one, leave voicemails",
      "Practices routinely leave $150K–$300K/yr of diagnosed tx in limbo",
    ],
  },
  {
    text: "Triage emergencies on the phone",
    icon: AlertTriangle,
    steps: [
      "Ask symptom questions to gauge severity (fever? swelling? bleeding?)",
      "Scan today’s schedule for anything moveable",
      "Call provider to see who can squeeze the patient in",
      "Call displaced patients to reschedule — all while the emergency waits on the line",
    ],
  },
  {
    text: "Greet & check in every patient",
    icon: UserCheck,
    steps: [
      "Confirm paperwork is complete at the counter",
      "Re-scan insurance cards, update demographics by hand",
      "Collect copays, process payment, reconcile at EOD",
      "Alert clinical team to changes before the patient sits down",
    ],
  },
  {
    text: "Send digital intake forms",
    icon: ClipboardList,
    steps: [
      "Email form link to every new patient",
      "Call at 24h and 48h if forms aren’t done",
      "Capture insurance + history verbally if patient can’t do digital",
      "Re-type responses into PMS from phone calls",
    ],
  },
  {
    text: "Gather & verify insurance info",
    icon: ShieldCheck,
    steps: [
      "Call patient pre-visit to collect card, ID, group #",
      "Call back if info is wrong or incomplete",
      "Re-enter into PMS, then run separate eligibility check",
      "Any mismatch blows up the claim weeks later",
    ],
  },
  {
    text: "Respond to after-hours messages",
    icon: Moon,
    steps: [
      "Arrive in the morning to a backlog of voicemails, texts, emails",
      "Manually triage and respond one by one",
      "Patients who messaged at night have already waited 12+ hrs",
      "Many give up and call a competitor who answered immediately",
    ],
  },
  {
    text: "Send appointment reminders",
    icon: MessageCircle,
    steps: [
      "Export schedule, send individual reminders via text/email/call",
      "Track confirmations in a spreadsheet",
      "Chase non-responders manually the day before",
      "One missed reminder = one open chair tomorrow",
    ],
  },
  {
    text: "Manage the waitlist manually",
    icon: Layers,
    steps: [
      "Keep a running spreadsheet of who’s flexible, for what, with what insurance",
      "Re-sort every time preferences change",
      "Match cancellations to waitlist entries by hand",
      "Becomes stale within days and gets ignored",
    ],
  },
  {
    text: "Drive post-visit surveys & Google reviews",
    icon: Star,
    steps: [
      "Manually email post-visit survey to each patient",
      "Aggregate results in a spreadsheet",
      "Identify unhappy patients — often too late",
      "Ask happy patients for reviews; most forget",
    ],
  },
  {
    text: "Monitor & respond to online reputation",
    icon: MessageCircle,
    steps: [
      "Check Google, Yelp, Facebook manually, often days late",
      "Draft individual responses and get manager sign-off",
      "Chase down root cause on every negative review",
      "One unaddressed 1-star review can cost $10K+ in new-patient LTV",
    ],
  },
  {
    text: "Handle HIPAA & consent paperwork",
    icon: FileText,
    steps: [
      "Print forms at check-in",
      "Walk each patient through every page",
      "Scan signed forms into the PMS manually",
      "Re-chase anything that got skipped",
    ],
  },
  {
    text: "Collect co-pays & process payments",
    icon: CreditCard,
    steps: [
      "Quote copay from memory or a printed cheat-sheet",
      "Swipe card or take cash at the counter",
      "Reconcile the terminal at end of day",
      "Quoting mistakes create refund calls and bad reviews later",
    ],
  },
  {
    text: "File incoming faxes into the PMS",
    icon: FileInput,
    steps: [
      "Walk to the fax machine, collect paper every few hours",
      "OCR or hand-transcribe into PMS",
      "Decide which chart each doc belongs to",
      "Categorize (EOB vs. referral vs. lab) and file correctly",
    ],
  },
];

const backOfficeTasks = [
  {
    text: "Log into each insurance portal for benefits",
    icon: Globe,
    steps: [
      "Log into 15–50 different payer portals, each with separate credentials",
      "Navigate to eligibility, pull benefit summary one patient at a time",
      "Download frequency tables, check for downgrade clauses",
      "Retype everything into the PMS benefit table",
    ],
  },
  {
    text: "Call insurance & wait on hold",
    icon: PhoneCall,
    steps: [
      "Dial payer line, fight through IVR menus",
      "Hold times routinely 30–60 minutes per call",
      "One patient = one call — staff can’t multitask effectively",
      "Ask CDT-specific coverage questions, document rep name + ref #",
    ],
  },
  {
    text: "Generate claims from clinical notes",
    icon: Send,
    steps: [
      "Read the day’s clinical notes, map to CDT codes manually",
      "Apply modifiers based on each payer’s rules",
      "Attach narratives, X-rays, pre-auth numbers",
      "One missed attachment = guaranteed denial",
    ],
  },
  {
    text: "Scrub claims against payer rules",
    icon: SearchCheck,
    steps: [
      "Cross-check every claim against bundling, frequency, and fee-schedule rules",
      "Remember 47+ payer-specific quirks from memory",
      "Catch missing surface designations, ICD-10 codes, pre-auth flags",
      "77% of denials come from preventable admin errors like this",
    ],
  },
  {
    text: "Submit claims & track adjudication",
    icon: Send,
    steps: [
      "Batch submit claims through the clearinghouse",
      "Monitor 999 acknowledgments manually",
      "Run weekly aging reports to catch pending claims",
      "Chase missing info before the timely-filing deadline runs out",
    ],
  },
  {
    text: "Post EOBs from ERAs & faxes",
    icon: FileInput,
    steps: [
      "Match every ERA to a claim in the PMS",
      "Verify each service line against contracted rates",
      "OCR scanned fax EOBs; fix gaps by hand",
      "One misposted EOB ripples into weeks of reconciliation work",
    ],
  },
  {
    text: "Reconcile payments to the penny",
    icon: Calculator,
    steps: [
      "Match bank deposits to EOBs to patient payments",
      "Track down EFTs that never hit the account",
      "Investigate mystery recoupments from prior claims",
      "Balance the ledger daily — any error multiplies over time",
    ],
  },
  {
    text: "Appeal claim denials",
    icon: AlertTriangle,
    steps: [
      "Decode payer denial codes (CO-50, CO-29, etc.)",
      "Pull perio charts, radiographs, narratives by hand",
      "Draft clinical appeals citing AAP / ADA guidelines",
      "67% of denied claims are never reworked — permanent lost revenue",
    ],
  },
  {
    text: "Fight wrongful denials with precedent",
    icon: FileSearch,
    steps: [
      "Prove timely filing with EDI transmission logs",
      "Cite ADA CDT date-of-service rules to the adjudicator",
      "Find prior-case precedents for the same payer",
      "Most offices don’t have the bandwidth — they just write it off",
    ],
  },
  {
    text: "Handle multi-payer COB puzzles",
    icon: Layers,
    steps: [
      "Apply Birthday Rule, Medicaid-last, and payer-specific overrides",
      "Catch primary-plan exclusions and elevate secondary when needed",
      "Explore medical crossover (CPT) for implants, grafts, pathology",
      "Triple-insurance cases can take hours per patient",
    ],
  },
  {
    text: "Submit & track pre-authorizations",
    icon: ClipboardList,
    steps: [
      "Compile narrative + photos + X-rays into a pre-auth packet",
      "Submit electronically, track status daily",
      "Chase reviewers who request additional info at day 8",
      "Delays push treatment weeks out and tank case acceptance",
    ],
  },
  {
    text: "Maintain CDT & ICD-10 coding knowledge",
    icon: Database,
    steps: [
      "Track yearly CDT code updates and retirements",
      "Learn each payer’s specific modifier and ICD-10 requirements",
      "Update internal cheat-sheets; retrain staff",
      "One code change missed = a month of rejected claims",
    ],
  },
  {
    text: "Catch underpayments vs. contracted rates",
    icon: PiggyBank,
    steps: [
      "Compare every EOB to the contracted fee schedule",
      "Detect amalgam downgrades applied to plans that don’t have them",
      "Submit reprocessing requests with plan-document proof",
      "Most offices miss these entirely and write off the gap",
    ],
  },
  {
    text: "Work aging AR by priority",
    icon: TrendingDown,
    steps: [
      "Pull aging report weekly, rank claims by dollar × days outstanding",
      "Call payers one by one for status on 60+ day claims",
      "File EFT traces for “paid” claims with no deposit",
      "Typical practice carries 5–10% of AR in 90+ bucket — slowly writing off",
    ],
  },
  {
    text: "Bill patients & call them to collect",
    icon: Receipt,
    steps: [
      "Generate statements, mail them out",
      "Call 60+ day patients one by one",
      "Explain coinsurance, offer payment plans on the fly",
      "Navigate sensitive situations (hardship, divorce, job loss) with no playbook",
    ],
  },
  {
    text: "Investigate patient billing disputes",
    icon: AlertTriangle,
    steps: [
      "Pull ledger, EOB, and original quote for the patient",
      "Figure out whether quote, posting, or insurance was wrong",
      "Decide on refund, adjustment, or escalation",
      "Every dispute costs 30–90 minutes of billing-staff time",
    ],
  },
  {
    text: "Trace missing EFT / payer payments",
    icon: FileSearch,
    steps: [
      "Identify claims marked “paid” with no deposit in the account",
      "Call payer to request trace number",
      "Cross-check bank records line by line",
      "Chase until the money actually lands, or write it off at 120 days",
    ],
  },
  {
    text: "OCR & reconcile scanned EOBs",
    icon: FileInput,
    steps: [
      "Run OCR on scanned/faxed EOBs — amounts are often unreadable",
      "Hand-fix OCR gaps using contracted fee schedule",
      "Trace unmatched recoupments back through old claims",
      "Reconcile every line before posting",
    ],
  },
  {
    text: "Year-end benefits maximization campaign",
    icon: Clock,
    steps: [
      "Run report of patients with unused benefits expiring Dec 31",
      "Call the highest-value ones personally",
      "Quote OOP on the spot, close the appointment",
      "Most practices can’t run this campaign at all — no bandwidth in Q4",
    ],
  },
  {
    text: "Manage servers & on-premise IT",
    icon: Server,
    steps: [
      "Run nightly backups of the PMS database",
      "Update Dentrix/Eaglesoft servers; deal with licensing renewals",
      "Troubleshoot imaging sensors, network drops, printer failures",
      "One server outage = a full day of lost production",
    ],
  },
];

/* ─── Hover Popover ─── */
const TaskPopover = ({ task, anchorRef, accent, onMouseEnter, onMouseLeave }) => {
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (!anchorRef?.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    const popW = 360;
    const popH = 260;
    const spaceBelow = window.innerHeight - rect.bottom;
    const top =
      spaceBelow > popH + 12
        ? rect.bottom + 6
        : Math.max(8, rect.top - popH - 6);
    const left = Math.min(rect.left, window.innerWidth - popW - 12);
    setPos({ top, left: Math.max(8, left) });
  }, [anchorRef]);

  const Icon = task.icon;

  return ReactDOM.createPortal(
    <motion.div
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      initial={{ opacity: 0, y: 6, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 6, scale: 0.96 }}
      transition={{ duration: 0.16, ease: "easeOut" }}
      style={{ position: "fixed", top: pos.top, left: pos.left, zIndex: 9999 }}
      className="w-[360px] bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden"
    >
      <div
        className={`px-4 py-2.5 border-b flex items-center gap-2`}
        style={{ backgroundColor: accent.bgLight, borderColor: accent.border }}
      >
        <div
          className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: accent.solid }}
        >
          <Icon className="w-3.5 h-3.5 text-white" />
        </div>
        <span className="text-[12px] font-bold text-slate-800 flex-1 leading-tight">
          {task.text}
        </span>
        <span
          className="text-[9px] font-bold px-2 py-0.5 rounded-full text-white"
          style={{ backgroundColor: accent.solid }}
        >
          {task.steps.length} steps
        </span>
      </div>
      <div className="px-3 py-2.5 space-y-1.5">
        {task.steps.map((step, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, x: -6 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: i * 0.04, duration: 0.2 }}
            className="flex items-start gap-2"
          >
            <div
              className="w-1.5 h-1.5 rounded-full flex-shrink-0 mt-1.5"
              style={{ backgroundColor: accent.solid }}
            />
            <span className="text-[11px] leading-relaxed text-slate-700">
              {step}
            </span>
          </motion.div>
        ))}
      </div>
    </motion.div>,
    document.body
  );
};

/* ─── Individual task card ─── */
const TaskCard = ({ task, index, accent }) => {
  const [isOpen, setIsOpen] = useState(false);
  const btnRef = useRef(null);
  const timeoutRef = useRef(null);

  const handleMouseEnter = () => {
    clearTimeout(timeoutRef.current);
    setIsOpen(true);
  };
  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => setIsOpen(false), 180);
  };

  const Icon = task.icon;

  return (
    <>
      <motion.div
        ref={btnRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05 + index * 0.015, duration: 0.3 }}
        className={`group relative flex items-center gap-2 rounded-lg border px-2.5 py-2 cursor-default transition-all duration-200 ${
          isOpen
            ? "bg-white shadow-md"
            : "bg-white/90 shadow-sm hover:shadow-md"
        }`}
        style={{
          borderColor: isOpen ? accent.solid : accent.border,
        }}
      >
        <div
          className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: accent.bgLight }}
        >
          <Icon className="w-3.5 h-3.5" style={{ color: accent.solid }} />
        </div>
        <span className="text-[11.5px] font-semibold text-slate-700 leading-snug flex-1 min-w-0">
          {task.text}
        </span>
        <div className="flex items-center gap-1 flex-shrink-0">
          <span
            className="text-[9px] font-bold px-1.5 py-0.5 rounded"
            style={{
              backgroundColor: isOpen ? accent.solid : accent.bgLight,
              color: isOpen ? "#fff" : accent.solid,
            }}
          >
            {task.steps.length}
          </span>
          <ChevronDown
            className="w-3 h-3 transition-transform duration-200"
            style={{
              color: accent.solid,
              transform: isOpen ? "rotate(180deg)" : "rotate(0)",
            }}
          />
        </div>
      </motion.div>
      <AnimatePresence>
        {isOpen && (
          <TaskPopover
            task={task}
            anchorRef={btnRef}
            accent={accent}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          />
        )}
      </AnimatePresence>
    </>
  );
};

const ManualTasksSlide = () => {
  const frontAccent = {
    solid: "#2563eb",
    bgLight: "#eff6ff",
    border: "#dbeafe",
  };
  const backAccent = {
    solid: "#4f46e5",
    bgLight: "#eef2ff",
    border: "#e0e7ff",
  };

  const totalTasks = frontOfficeTasks.length + backOfficeTasks.length;
  const totalSteps =
    frontOfficeTasks.reduce((s, t) => s + t.steps.length, 0) +
    backOfficeTasks.reduce((s, t) => s + t.steps.length, 0);

  return (
    <div className="h-full w-full bg-slate-50 flex flex-col p-5 relative overflow-hidden">
      {/* Background */}
      <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-blue-100/40 rounded-full blur-[120px] -translate-y-1/3 -translate-x-1/4" />
      <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-indigo-100/40 rounded-full blur-[100px] translate-y-1/3 translate-x-1/4" />

      <div className="max-w-[1350px] w-full mx-auto flex flex-col h-full relative z-10">
        {/* Header */}
        <div className="text-center mb-3 flex-shrink-0">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-3xl lg:text-5xl font-extrabold text-slate-900 mb-3 tracking-tight leading-[1.1]">
              Even with so much software, staff has to carry out{" "}
              <span className="text-blue-600">40+ tasks</span> across the patient journey
            </h2>
          </motion.div>
        </div>

        {/* Two columns: Front / Back Office */}
        <div className="flex-1 min-h-0 grid grid-cols-2 gap-4">
          {/* Front Office */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-white/70 backdrop-blur-sm rounded-2xl border border-blue-200 shadow-sm p-4 flex flex-col min-h-0"
          >
            <div className="flex items-center gap-3 mb-3 flex-shrink-0">
              <div className="w-9 h-9 rounded-xl bg-blue-100 flex items-center justify-center">
                <Headphones className="w-4.5 h-4.5 text-blue-600" />
              </div>
              <div>
                <h3 className="text-[15px] font-bold text-slate-900">
                  Front Office Tasks
                </h3>
                <p className="text-[10.5px] text-slate-400 font-medium">
                  Phones, scheduling, patient communication
                </p>
              </div>
              <div className="ml-auto px-2.5 py-1 rounded-full bg-blue-50 border border-blue-100">
                <span className="text-[10px] font-bold text-blue-600">
                  {frontOfficeTasks.length} tasks
                </span>
              </div>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto pr-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <div className="grid grid-cols-2 gap-1.5">
                {frontOfficeTasks.map((task, i) => (
                  <TaskCard
                    key={i}
                    task={task}
                    index={i}
                    accent={frontAccent}
                  />
                ))}
              </div>
            </div>
          </motion.div>

          {/* Back Office */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="bg-white/70 backdrop-blur-sm rounded-2xl border border-indigo-200 shadow-sm p-4 flex flex-col min-h-0"
          >
            <div className="flex items-center gap-3 mb-3 flex-shrink-0">
              <div className="w-9 h-9 rounded-xl bg-indigo-100 flex items-center justify-center">
                <DollarSign className="w-4.5 h-4.5 text-indigo-600" />
              </div>
              <div>
                <h3 className="text-[15px] font-bold text-slate-900">
                  Back Office Tasks
                </h3>
                <p className="text-[10.5px] text-slate-400 font-medium">
                  Insurance, billing, claims, IT
                </p>
              </div>
              <div className="ml-auto px-2.5 py-1 rounded-full bg-indigo-50 border border-indigo-100">
                <span className="text-[10px] font-bold text-indigo-600">
                  {backOfficeTasks.length} tasks
                </span>
              </div>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto pr-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <div className="grid grid-cols-2 gap-1.5">
                {backOfficeTasks.map((task, i) => (
                  <TaskCard
                    key={i}
                    task={task}
                    index={i}
                    accent={backAccent}
                  />
                ))}
              </div>
            </div>
          </motion.div>
        </div>

        {/* Bottom stats */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="flex items-center justify-center gap-4 mt-3 flex-shrink-0"
        >
          {[
            {
              icon: Phone,
              text: "Hours of phone time per day",
              color: "text-blue-600",
              bg: "bg-blue-50",
              border: "border-blue-100",
            },
            {
              icon: TrendingDown,
              text: "Human error & burnout compound daily",
              color: "text-indigo-600",
              bg: "bg-indigo-50",
              border: "border-indigo-100",
            },
            {
              icon: AlertTriangle,
              text: "Most tasks drop at 5pm, weekends, lunch",
              color: "text-blue-600",
              bg: "bg-blue-50",
              border: "border-blue-100",
            },
          ].map((stat, i) => (
            <div
              key={i}
              className={`flex items-center gap-2 px-3.5 py-2 rounded-xl ${stat.bg} border ${stat.border}`}
            >
              <stat.icon className={`w-3.5 h-3.5 ${stat.color}`} />
              <span className="text-[11px] font-semibold text-slate-700">
                {stat.text}
              </span>
            </div>
          ))}
        </motion.div>
      </div>
    </div>
  );
};

export default ManualTasksSlide;
